import { Component, Input, OnInit, Optional } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';

import { of } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { MortgageService } from '../../../mortgage/services/mortgage/mortgage.service';
import { ModalComponent } from '../../../vass-core/components/modal/modal.component';
import { AdvanceSimulatorModel } from '../../models/advance-simulator.model';
import { RegisterModel } from '../../models/register.model';
import { AuthService } from '../../services/auth/auth.service';
import { UserDataService } from '../../services/user-data/user-data.service';
import {
  EMAIL_REGEXP,
  ES_MOBILE_PHONE_REGEXP,
  LAYOUT_NAME,
  SUMMARY_SECOND_HOLDER,
} from '../../utils/constants';
import { CustomValidators } from '../../utils/custom-validators';

import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from 'file-saver';
import { catchError, map, switchMap } from 'rxjs/operators';
import { MortgageDataModel } from '../../../mortgage/models/mortgage-data.model';
import { GtmService } from '../../services/gtm/gtm.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginComponent implements OnInit {
  @Input() loginSecondHolder: boolean = false;
  public loginForm: UntypedFormGroup;
  public otpForm: UntypedFormGroup;
  public showOtp: boolean;
  public keyCaptcha: string;
  public idOTP: string;
  public otpError: boolean = false;
  public isLoginFailed: boolean;
  public messageError: string = '';

  public isRegister: boolean = false;
  public openFromHeader: boolean = false;
  public showTitle: boolean = false;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private _userDataService: UserDataService,
    private _authServive: AuthService,
    private _mortgageService: MortgageService,
    private translate: TranslateService,
    private gtm: GtmService,
    @Optional() private modalComponent: ModalComponent,
  ) {
    if (this.modalComponent) {
      this.isRegister = this.modalComponent.data.isRegister
        ? this.modalComponent.data.isRegister
        : false;
      this.openFromHeader = this.modalComponent.data.openFromHeader
        ? this.modalComponent.data.openFromHeader
        : false;
    } else {
      this.showTitle = true;
    }
    this.showOtp = false;
  }

  ngOnInit() {
    this.keyCaptcha = environment.key_captcha;
    this.createLoginForm();
    this.createOtpForm();
  }

  createLoginForm() {
    this._userDataService.isRegister = this.isRegister;
    if (this.isRegister) {
      this.loginForm = this.formBuilder.group(
        {
          phone: ['', [Validators.required, Validators.pattern(ES_MOBILE_PHONE_REGEXP)]],
          idNumberTit: [
            '',
            Validators.required,
            CustomValidators.validateDocument('BOTH'),
          ],
          email: ['', [Validators.required, Validators.pattern(EMAIL_REGEXP)]],
          repeatEmail: ['', [Validators.required, Validators.pattern(EMAIL_REGEXP)]],
          captcha: ['', [Validators.required]],
          protection_clauses_1: [false, Validators.required, this.isChecked.bind(this)],
          protection_clauses_2: [false, Validators.required, this.isChecked.bind(this)],
        },
        { validator: this.validateRepeatEmail },
      );
      this.openFromHeader
        ? this.gtm.sendEventGTM({
            event: 'interaccion',
            eAction: 'simulador_avanzado',
            eLabel: 'abrir',
          })
        : this.gtm.sendEventGTM({
            event: 'interaccion',
            eAction: 'solicitar_estudio_personalizado',
            eLabel: 'abrir',
          });
    } else {
      this.loginForm = this.formBuilder.group({
        email: ['', [Validators.required, Validators.pattern(EMAIL_REGEXP)]],
      });
      this.gtm.sendEventGTM({
        event: 'interaccion',
        eAction: 'recuperar_solicitud',
        eLabel: 'abrir',
      });
    }
  }

  isChecked(control: AbstractControl) {
    let respuesta;
    if (!control.value) {
      respuesta = { validateUsername: control.value };
    }
    return of(respuesta);
  }

  createOtpForm() {
    this.otpForm = this.formBuilder.group({
      code: ['', Validators.required],
    });
  }

  generateOTP() {
    if (this.loginForm.valid) {
      if (this.isRegister) {
        const userData: RegisterModel = {
          idNumber: this.loginForm.controls.idNumberTit
            ? this.loginForm.controls.idNumberTit.value
            : '',
          email: this.loginForm.controls.email.value,
          phone: this.loginForm.controls.phone.value,
          isAllowedPersonalData: null,
          isAllowedPublicity: null,
          isAllowedPrivacyPolicy: true,
          isAllowedInsuranceCost: true,
          origin: environment.origin,
          officeCode: '', // no tenemos office code en PiBank
        };
        this.register(userData);
        this.openFromHeader
          ? this.gtm.sendEventGTM({
              event: 'interaccion',
              eAction: 'simulador_avanzado',
              eLabel: 'continuar',
            })
          : this.gtm.sendEventGTM({
              event: 'interaccion',
              eAction: 'solicitar_estudio_personalizado',
              eLabel: 'continuar',
            });
      } else {
        const userData: AdvanceSimulatorModel = {
          email: this.loginForm.controls.email.value,
        };
        this.login(userData);
        this.gtm.sendEventGTM({
          event: 'interaccion',
          eAction: 'recuperar_solicitud',
          eLabel: 'continuar',
        });
      }
    } else {
      for (const campo in this.loginForm.controls) {
        this.loginForm.controls[campo].markAsTouched();
      }
    }
  }

  confirmationOTP() {
    if (this.otpForm.valid) {
      const body = {
        id: this.idOTP,
        otp: this.otpForm.controls.code.value,
      };

      this._authServive
        .validateOTP(body, this.loginSecondHolder)
        .pipe(
          switchMap((response) =>
            this.gtm.getUserIdGTM(this.loginForm.controls.email.value).pipe(
              map(() => response),
              catchError(() => of(response)),
            ),
          ),
        )
        .subscribe(
          (data: any) => {
            sessionStorage.setItem('caseId', btoa(this.idOTP));
            this._userDataService.setLevel2Data({
              idNumber: this.loginForm.controls.idNumberTit
                ? this.loginForm.controls.idNumberTit.value
                : '',
              email: this.loginForm.controls.email.value,
            });

            const userData: any = data;
            this._userDataService.setUserData(userData);
            this._authServive.setAuthenticated(true);

            if (this.modalComponent) {
              this.modalComponent.reject();
            }
            if (this._userDataService.getIsFromSimulate()) {
              const userSimulationData = this._userDataService.getUserData();
              const userSimulationDataMapped: MortgageDataModel = {
                numberHolders: '',
                savingsMade: userSimulationData.level1Data.savingsMade,
                mortgageAmount: userSimulationData.level1Data.mortgageAmount,
                termInYears: userSimulationData.level1Data.termInYears,
                isEcuadorSegment: false,
                signatureDate: '',
                homeData: {
                  isSubsidizedHousing: userSimulationData.level1Data.isSubsidizedHousing,
                  isNewHome: userSimulationData.level1Data.isNewHome,
                  isFirsthome: true,
                  propertyPrice: userSimulationData.level1Data.propertyPrice,
                  location: userSimulationData.level1Data.location,
                  zipCode: '',
                  squareMeters: '',
                  operationType: 1,
                },
              };
              this._mortgageService.saveData(userSimulationDataMapped).subscribe();
              this._userDataService.setIsFromSimulate(false);
            }
            if (this.loginSecondHolder) {
              this.router.navigate([SUMMARY_SECOND_HOLDER]);
              sessionStorage.setItem(
                'second-holder',
                this.loginForm.controls.email.value,
              );
            } else {
              this.router.navigate([LAYOUT_NAME]);
            }
          },
          (err) => {
            this.messageError = err && err.message ? 'error_' + err.message : '';
            if (this.translate.instant(this.messageError) === this.messageError) {
              this.messageError = 'error_GENERIC';
            }
            this.otpError = true;
          },
        );
      if (this.isRegister) {
        this.openFromHeader
          ? this.gtm.sendEventGTM({
              event: 'interaccion',
              eAction: 'simulador_avanzado',
              eLabel: 'codigo_sms',
            })
          : this.gtm.sendEventGTM({
              event: 'interaccion',
              eAction: 'solicitar_estudio_personalizado',
              eLabel: 'codigo_sms',
            });
      } else {
        this.gtm.sendEventGTM({
          event: 'interaccion',
          eAction: 'recuperar_solicitud',
          eLabel: 'codigo_sms',
        });
      }
    } else {
      this.otpForm.controls.code.markAsTouched();
    }
  }

  resolved(captchaResponse: string) {
    this.loginForm.controls['captcha'].setValue(captchaResponse);
  }

  login(body: any) {
    this._authServive.login(body, this.loginSecondHolder).subscribe(
      ({ id }) => {
        this.showOtp = true;
        this.restoreValidation();
        this.idOTP = id;
      },
      (err) => {
        if (err && (err.status === 504 || err.status === 503 || err.status === 500)) {
          this.messageError = 'error_GENERIC';
        } else {
          this.messageError = err && err.message ? 'error_' + err.message : '';
          if (this.translate.instant(this.messageError) === this.messageError) {
            this.messageError = 'error_GENERIC';
          }
        }
        this.isLoginFailed = true;
      },
    );
  }

  register(body: any) {
    this._authServive.registerCase(body).subscribe(
      ({ id }) => {
        this.showOtp = true;
        this.idOTP = id;
      },
      (err) => {
        if (err && (err.status === 504 || err.status === 503 || err.status === 500)) {
          this.messageError = 'error_GENERIC';
        } else {
          this.messageError = err && err.message ? 'error_' + err.message : '';
          if (err.message === 'login_7') {
            this.messageError = 'error_login_7_hipo_otp';
          }
        }
        this.isLoginFailed = true;
      },
    );
  }

  restoreValidation() {
    this.isLoginFailed = false;
  }

  public openToast(id: string) {
    const elementHTML = document.getElementById(id);
    elementHTML.blur();
    elementHTML.focus();
  }

  private validateRepeatEmail(loginForm: UntypedFormGroup) {
    const email = loginForm.controls.email.value.toLocaleLowerCase();
    const repeatEmail = loginForm.controls.repeatEmail.value.toLocaleLowerCase();
    return email === repeatEmail ? null : { invalidRepearEmail: true };
  }

  savePDF() {
    const oReq = new XMLHttpRequest();
    // The Endpoint of your server
    const URLToPDF = 'https://img.pibank.es/pdfs/PoliticaProteccionDatos.pdf';

    // Configure XMLHttpRequest
    oReq.open('GET', URLToPDF, true);

    // Important to use the blob response type
    oReq.responseType = 'blob';

    // When the file request finishes
    // Is up to you, the configuration for error events etc.
    oReq.onload = function () {
      // Once the file is downloaded, open a new window with the PDF
      // Remember to allow the POP-UPS in your browser
      const file = new Blob([oReq.response], {
        type: 'application/pdf',
      });

      // Generate file download directly in the browser !
      FileSaver.saveAs(file, 'PoliticaProteccionDatos.pdf');
    };

    oReq.send();
  }
}
